/**
 * PATH: packages/ui/src/kanban/components/lane/useTouchDragCard.ts
 * FILE_ID: UI-KANBAN-LANE-TOUCHDRAG-001
 * PURPOSE: Touch drag-and-drop for Kanban cards — hold ~250ms then move past
 *          slop starts a drag (ghost clone follows the finger); drop over a
 *          `[data-lane-id]` fires the SAME onCardDrop path as HTML5 DnD.
 * WHY: Native HTML5 `draggable` never fires on touch, so phones could only
 *      move cards via the long-press menu. Coexists with the long-press
 *      gesture in useKanbanCardInteractions — a STATIONARY hold still acts
 *      (context menu on phones, quick-action rail on tablets), and its own
 *      movement-cancel kills it once a drag moves. We cede the gesture at
 *      LONG_PRESS_MS stationary (MENU_TAKEOVER_MS). That used to be a pair of
 *      matching 500ms literals in two files — the "LANDMINE" this header
 *      warned about — and is now derived from ../kanbanTouchGesture, so the
 *      two hooks cannot drift apart.
 * FLOW: touchstart ─▶ hold(250ms) ─▶ move>slop ─▶ drag ghost + hit-test
 *       ─▶ touchend over lane ─▶ registry.drop(cardId, position) (same
 *       cardReorder math + host mutation the desktop drop uses).
 * RELATED: touchDragRegistry (UI-KANBAN-LANE-TOUCHREG-001); cardReorder.
 */
import { useCallback, useEffect, useRef } from 'react'
import type { TouchEvent as ReactTouchEvent } from 'react'

import type { Card } from '../../types'
import {
  computeDropIndexFromMidpoints,
  computeReorderPosition,
} from '../../utils/cardReorder'
import { LONG_PRESS_MS, TOUCH_MOVE_CANCEL_PX } from '../kanbanTouchGesture'
import { getTouchDropLane } from './touchDragRegistry'

/** Hold this long before a move is a drag (below it, movement = scroll).
 *  Must stay strictly BELOW LONG_PRESS_MS: the finger has to be able to become
 *  a drag before the hold gesture claims it, or cards could never be dragged. */
export const TOUCH_DRAG_HOLD_MS = 250
/** Movement past this (px) after the hold starts the drag; before the hold
 *  it cancels the gesture (finger is scrolling). Re-exported from the shared
 *  gesture budget so it cannot drift from the hold's own movement-cancel. */
export const TOUCH_DRAG_SLOP_PX = TOUCH_MOVE_CANCEL_PX
/** Once the stationary hold acts (context menu on phones, quick-action rail on
 *  tablets) it owns the gesture, and we must not start a drag underneath it.
 *  Derived, not restated: this and the hold timer were separate literals that
 *  agreed only by luck — see kanbanTouchGesture for why that mattered. */
export const MENU_TAKEOVER_MS = LONG_PRESS_MS
const EDGE_ZONE_PX = 48
const SCROLL_STEP_PX = 12

const LANE_OVER_OUTLINE = '2px solid rgba(59,130,246,0.65)'

/** Same measurement + math as KanbanLane's HTML5 onDrop — the previewed
 *  landing index can never disagree with the desktop path. */
function computeTouchDropPosition(
  laneEl: HTMLElement,
  clientY: number,
  laneCards: readonly Card[],
  movingCardId: string,
): number | undefined {
  const tiles = Array.from(
    laneEl.querySelectorAll<HTMLElement>('[data-card-id]'),
  )
  if (tiles.length === 0) return undefined
  const orderedIds = tiles
    .map((el) => el.getAttribute('data-card-id'))
    .filter((id): id is string => !!id)
  const midpoints = tiles.map((el) => {
    const r = el.getBoundingClientRect()
    return r.top + r.height / 2
  })
  const dropIndex = computeDropIndexFromMidpoints(clientY, midpoints)
  const byId = new Map(laneCards.map((c) => [c.id, c]))
  const orderedCards = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is Card => !!c)
  return computeReorderPosition(orderedCards, dropIndex, movingCardId)
}

/** Nearest horizontal scroller for edge auto-scroll (flat board canvas
 *  carries data-mobile-canvas; fall back to a computed-style walk). */
function findHorizontalScroller(from: HTMLElement): HTMLElement | null {
  const canvas = from.closest<HTMLElement>('[data-mobile-canvas]')
  if (canvas) return canvas
  let el: HTMLElement | null = from.parentElement
  while (el) {
    try {
      const s = window.getComputedStyle(el)
      if (s.overflowX === 'auto' || s.overflowX === 'scroll') return el
    } catch {
      return null
    }
    el = el.parentElement
  }
  return null
}

export interface UseTouchDragCardArgs {
  card: Card
  /** False when the host wired no onCardDrop (read-only board) — inert. */
  enabled: boolean
  /** True when a stationary hold has something to open — the context menu on
   *  phones, the quick-action rail on tablets. The hold owns the gesture from
   *  MENU_TAKEOVER_MS, so the drag arm is released then. (Named for the menu
   *  because that is what the board wires; the board always passes true, so
   *  the tablet rail is covered by the same release.) */
  contextMenuWired: boolean
}

export interface UseTouchDragCardResult {
  onTouchStart: (e: ReactTouchEvent<HTMLElement>) => void
}

export function useTouchDragCard({
  card,
  enabled,
  contextMenuWired,
}: UseTouchDragCardArgs): UseTouchDragCardResult {
  // Live cleanup for the in-flight gesture (unmount mid-drag must not leak
  // window listeners, the ghost node, or the rAF loop).
  const cleanupRef = useRef<(() => void) | null>(null)
  useEffect(() => () => cleanupRef.current?.(), [])

  // Stable identity payload so the (stable) handler reads fresh card fields.
  const cardRef = useRef(card)
  cardRef.current = card

  const onTouchStart = useCallback(
    (e: ReactTouchEvent<HTMLElement>) => {
      if (!enabled) return
      if (e.touches.length !== 1) return
      if (cardRef.current.access_locked) return
      // One gesture at a time — a second touchstart mid-gesture is noise.
      if (cleanupRef.current) return
      const cardEl = (e.target as HTMLElement | null)?.closest?.(
        '[data-card-id]',
      ) as HTMLElement | null
      if (!cardEl) return
      const startX = e.touches[0].clientX
      const startY = e.touches[0].clientY
      const cardId = cardRef.current.id
      const placementId = cardRef.current.placement_id ?? undefined

      let armed = false // hold delay elapsed, movement now starts a drag
      let dragging = false
      let ghost: HTMLElement | null = null
      let overLane: HTMLElement | null = null
      let lastX = startX
      let lastY = startY
      let rafId: number | null = null
      let hScroller: HTMLElement | null = null
      let savedSnap = ''
      let savedOutline = ''
      let savedOutlineOffset = ''

      const clearOverLane = () => {
        if (!overLane) return
        overLane.style.outline = savedOutline
        overLane.style.outlineOffset = savedOutlineOffset
        overLane.removeAttribute('data-touch-drag-over')
        overLane = null
      }

      const cleanup = () => {
        clearTimeout(armTimer)
        clearTimeout(menuTimer)
        window.removeEventListener('touchmove', onMove)
        window.removeEventListener('touchend', onEnd)
        window.removeEventListener('touchcancel', onCancel)
        if (rafId != null) cancelAnimationFrame(rafId)
        rafId = null
        if (ghost) ghost.remove()
        ghost = null
        clearOverLane()
        if (hScroller) hScroller.style.scrollSnapType = savedSnap
        hScroller = null
        cleanupRef.current = null
      }

      const startDrag = () => {
        dragging = true
        const rect = cardEl.getBoundingClientRect()
        ghost = cardEl.cloneNode(true) as HTMLElement
        ghost.setAttribute('data-touch-drag-ghost', '')
        ghost.setAttribute('aria-hidden', 'true')
        Object.assign(ghost.style, {
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          margin: '0',
          zIndex: '9999',
          opacity: '0.85',
          pointerEvents: 'none',
          transform: 'rotate(2deg) scale(1.03)',
          transition: 'none',
        } as Partial<CSSStyleDeclaration>)
        document.body.appendChild(ghost)
        cardEl.setAttribute('data-touch-dragging', 'true')
        // Scroll-snap fights programmatic edge auto-scroll — pause it.
        hScroller = findHorizontalScroller(cardEl)
        if (hScroller) {
          savedSnap = hScroller.style.scrollSnapType
          hScroller.style.scrollSnapType = 'none'
        }
        rafId = requestAnimationFrame(autoScrollTick)
      }

      const autoScrollTick = () => {
        if (!dragging) return
        if (hScroller) {
          const r = hScroller.getBoundingClientRect()
          if (lastX < r.left + EDGE_ZONE_PX) hScroller.scrollLeft -= SCROLL_STEP_PX
          else if (lastX > r.right - EDGE_ZONE_PX) hScroller.scrollLeft += SCROLL_STEP_PX
        }
        // Vertical auto-scroll inside the hovered lane's card scroller.
        const vScroller = overLane?.querySelector<HTMLElement>('[data-lane-scroll]')
        if (vScroller) {
          const r = vScroller.getBoundingClientRect()
          if (lastY < r.top + EDGE_ZONE_PX) vScroller.scrollTop -= SCROLL_STEP_PX
          else if (lastY > r.bottom - EDGE_ZONE_PX) vScroller.scrollTop += SCROLL_STEP_PX
        }
        rafId = requestAnimationFrame(autoScrollTick)
      }

      const hitTestLane = (x: number, y: number): HTMLElement | null => {
        const el = document.elementFromPoint?.(x, y) as HTMLElement | null
        return (el?.closest?.('[data-lane-id]') as HTMLElement | null) ?? null
      }

      const onMove = (mv: TouchEvent) => {
        const t = mv.touches[0]
        if (!t) return
        lastX = t.clientX
        lastY = t.clientY
        const dx = t.clientX - startX
        const dy = t.clientY - startY
        const past = Math.abs(dx) > TOUCH_DRAG_SLOP_PX || Math.abs(dy) > TOUCH_DRAG_SLOP_PX
        if (!dragging) {
          if (!past) return
          // Movement before the hold = a scroll/swipe: stand down entirely.
          if (!armed) {
            cleanup()
            return
          }
          startDrag()
        }
        // Dragging: own the gesture (stop the page/lane scrolling under it).
        if (mv.cancelable) mv.preventDefault()
        if (ghost) ghost.style.transform = `translate(${dx}px, ${dy}px) rotate(2deg) scale(1.03)`
        const lane = hitTestLane(t.clientX, t.clientY)
        if (lane !== overLane) {
          clearOverLane()
          if (lane) {
            overLane = lane
            savedOutline = lane.style.outline
            savedOutlineOffset = lane.style.outlineOffset
            lane.style.outline = LANE_OVER_OUTLINE
            lane.style.outlineOffset = '-2px'
            lane.setAttribute('data-touch-drag-over', 'true')
          }
        }
      }

      const onEnd = (ev: TouchEvent) => {
        if (!dragging) {
          cleanup()
          return
        }
        // Swallow the touchend's synthetic click so the drop never ALSO
        // opens the card drawer (same shape as the long-press click-swallow).
        if (ev.cancelable) ev.preventDefault()
        const swallow = (ce: MouseEvent) => {
          ce.preventDefault()
          ce.stopPropagation()
        }
        window.addEventListener('click', swallow, { capture: true, once: true })
        setTimeout(
          () => window.removeEventListener('click', swallow, { capture: true }),
          400,
        )
        const t = ev.changedTouches[0]
        const dropX = t?.clientX ?? lastX
        const dropY = t?.clientY ?? lastY
        const laneEl = hitTestLane(dropX, dropY)
        cardEl.removeAttribute('data-touch-dragging')
        if (laneEl) {
          const laneId = laneEl.getAttribute('data-lane-id')
          const entry = laneId ? getTouchDropLane(laneId) : undefined
          if (entry) {
            const position = computeTouchDropPosition(
              laneEl,
              dropY,
              entry.getCards(),
              cardId,
            )
            entry.drop(cardId, position, placementId)
          }
        }
        cleanup()
      }

      const onCancel = () => {
        cardEl.removeAttribute('data-touch-dragging')
        cleanup()
      }

      const armTimer = setTimeout(() => {
        armed = true
      }, TOUCH_DRAG_HOLD_MS)
      // Stationary past 500ms with a menu wired → the long-press context
      // menu (useKanbanCardInteractions) owns the gesture; release our arm
      // so a move with the menu open can never start a drag beneath it.
      const menuTimer = contextMenuWired
        ? setTimeout(() => {
            if (!dragging) cleanup()
          }, MENU_TAKEOVER_MS)
        : undefined

      // passive:false so an in-drag move can preventDefault the scroll; the
      // long-press menu's own (passive) listeners are unaffected.
      window.addEventListener('touchmove', onMove, { passive: false })
      window.addEventListener('touchend', onEnd)
      window.addEventListener('touchcancel', onCancel)
      cleanupRef.current = cleanup
    },
    [enabled, contextMenuWired],
  )

  return { onTouchStart }
}
